import { AnimatePresence, motion } from "framer-motion";
import { PlusIcon } from "lucide-react";
import type { ReactNode } from "react";

import { fadeSlideVariants, springGentle } from "../share-session-dialog-constants";

import { Button } from "@/components/ui/button";
import { DialogClose, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import type { SharedSession } from "@/queries/prompts";

interface SharedSessionManageViewLayoutProps {
  sessions: SharedSession[] | undefined;
  isLoadingSessions: boolean;
  onNewLink: () => void;
  /** Rendered inside the scroll area when sessions are loaded (e.g. mapped session rows). */
  sessionRows: ReactNode;
}

export const SharedSessionManageViewLayout = ({
  sessions,
  isLoadingSessions,
  onNewLink,
  sessionRows,
}: SharedSessionManageViewLayoutProps) => (
  <motion.div
    key="manage"
    variants={fadeSlideVariants}
    initial="initial"
    animate="animate"
    exit="exit"
    transition={springGentle}
    className="flex min-w-0 flex-col overflow-hidden"
  >
    <div className="px-6 pb-2 pt-6">
      <DialogHeader>
        <DialogTitle className="text-base">
          Share Links {sessions?.length ? `(${sessions.length})` : ""}
        </DialogTitle>
      </DialogHeader>
    </div>

    <div className="scrollbar-thin flex max-h-[380px] min-w-0 flex-col gap-2 overflow-y-auto overflow-x-hidden px-4 py-2">
      {isLoadingSessions ? (
        <div className="flex items-center justify-center py-8">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
            className="h-5 w-5 rounded-full border-2 border-gray-200 border-t-gray-500"
          />
        </div>
      ) : (
        <AnimatePresence initial={false}>{sessionRows}</AnimatePresence>
      )}
    </div>

    <Separator />

    <div className="flex items-center justify-between px-6 py-3">
      <Button
        variant="ghost"
        size="sm"
        className="gap-1.5 text-gray-500 hover:text-gray-700"
        onClick={onNewLink}
      >
        <PlusIcon className="h-3.5 w-3.5" />
        New link
      </Button>
      <DialogClose asChild>
        <Button variant="outline" size="sm" className="rounded-lg">
          Done
        </Button>
      </DialogClose>
    </div>
  </motion.div>
);
